import ResponsiveImage from '@/components/Image/ResponsiveImage';
import Link from '@/components/Link/Link';
import type { FrontifyImageContent } from '@/contentful/contentful-exports';
import useGlobalStyles from '@/styles/global.styles';

import useStyles from './AuthorBio.styles';

interface AuthorBioContent {
  name?: string;
  image?: [FrontifyImageContent];
  imageAltText?: string;
  bio?: string;
  link?: string;
}

export default function AuthorBio({
  name,
  image,
  imageAltText,
  bio,
  link,
}: AuthorBioContent): React.JSX.Element | null {
  const { classes, cx } = useStyles();
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });

  // Nothing worth showing without a name, and nothing worth showing beyond
  // the existing byline unless there's a bio, a link, or a photo to add.
  if (!name || (!bio && !link && !image?.[0])) {
    return null;
  }

  const authorImage = image?.[0] && (
    <ResponsiveImage
      sizes={{ xs: 64 }}
      src={image[0]}
      alt={imageAltText || name}
      className={classes.image}
    />
  );

  const nameClassName = cx(globalClasses.body, classes.name);
  const authorName = link ? (
    <Link href={link} className={cx(nameClassName, classes.nameLink)}>
      {name}
    </Link>
  ) : (
    <span className={nameClassName}>{name}</span>
  );

  return (
    <section
      className={cx(classes.authorBio, bio && classes.card)}
      aria-label='Author bio'
    >
      {authorImage}
      <div className={classes.textWrapper}>
        {authorName}
        {bio && <p className={cx(globalClasses.body, classes.bio)}>{bio}</p>}
      </div>
    </section>
  );
}
